/**
 * @file useBookingFlow.js — shared booking state machine for both public and authenticated flows.
 *
 * Manages the multi-step booking process:
 *   1. Select location (auto-selects if only one)
 *   2. Select service (auto-selects if only one)
 *   3. Select month → fetch available dates
 *   4. Select date → fetch time slots
 *   5. Book appointment
 *
 * Works with two modes:
 *   - "public" (slug-based, uses publicService — for /c/:slug)
 *   - "auth" (id-based, uses publicService for slots + appointmentService for booking — for /profesional/:id)
 *
 * @module hooks/useBookingFlow
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { publicService } from "../services/public";
import { appointmentService } from "../services/appointment";

/**
 * Shared booking flow hook.
 *
 * @param {Object} options
 * @param {Object|null} options.professional - Professional data (must include locations, sessionDuration, slug, id)
 * @param {"public"|"auth"} [options.mode="public"] - Booking mode
 * @param {number|null} [options.patientId=null] - Patient ID (required for auth mode)
 * @returns {Object} Booking state and actions
 */
export function useBookingFlow({ professional, mode = "public", patientId = null }) {
  // Step 1: location selection
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Step 2: service selection
  const [selectedService, setSelectedService] = useState(null);

  // Step 3: month selection ("YYYY-MM")
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Step 3: available days in selected month
  const [availableDates, setAvailableDates] = useState(null);
  const [datesLoading, setDatesLoading] = useState(false);

  // Step 4: day + time slot selection
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Next available slot (for inline waitlist prompt)
  const [nextAvailable, setNextAvailable] = useState(null);
  const [nextAvailableLoading, setNextAvailableLoading] = useState(false);

  // Booking
  const [isBooking, setIsBooking] = useState(false);

  const slug = professional?.slug;

  // Auto-select if only one location
  useEffect(() => {
    if (!professional) return;
    if (professional.locations?.length === 1 && !selectedLocation) {
      setSelectedLocation(professional.locations[0]);
    }
  }, [professional, selectedLocation]);

  // Auto-select if only one active service
  // API returns "consultationServices" (public) or "services" (auth) depending on the endpoint
  useEffect(() => {
    if (!professional) return;
    const allServices = professional.consultationServices || professional.services || [];
    const activeServices = allServices.filter((s) => s.isActive);
    if (activeServices.length === 1 && !selectedService) {
      setSelectedService(activeServices[0]);
    }
  }, [professional, selectedService]);

  // When location is selected → fetch next available date + first slot (for waitlist prompt)
  useEffect(() => {
    if (!slug || !selectedLocation) { setNextAvailable(null); return; }
    const today = new Date();
    const dayOffset = professional?.allowSameDayBooking ? 0 : 1;
    const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() + dayOffset);
    const to = new Date(from); to.setDate(to.getDate() + 60);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    setNextAvailableLoading(true);
    publicService.getAvailableDates(slug, fmt(from), fmt(to), selectedLocation.id)
      .then((res) => {
        const dates = res.data || [];
        if (dates.length > 0) {
          return publicService.getSlots(slug, dates[0], selectedLocation.id)
            .then((slotRes) => {
              const firstSlot = (slotRes.data || [])[0];
              setNextAvailable(firstSlot ? { date: dates[0], time: firstSlot.start } : { date: dates[0], time: null });
            });
        } else {
          setNextAvailable(null);
        }
      })
      .catch(() => setNextAvailable(null))
      .finally(() => setNextAvailableLoading(false));
  }, [slug, selectedLocation, professional]);

  // When location or month changes → fetch available dates
  useEffect(() => {
    if (!slug || !selectedLocation || !selectedMonth) {
      setAvailableDates(null);
      return;
    }

    const [year, month] = selectedMonth.split("-").map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const today = new Date();
    const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;
    const dayOffset = professional?.allowSameDayBooking ? 0 : 1;
    const fromDate = isCurrentMonth
      ? new Date(today.getFullYear(), today.getMonth(), today.getDate() + dayOffset)
      : new Date(year, month - 1, 1);
    const from = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, "0")}-${String(fromDate.getDate()).padStart(2, "0")}`;
    const to = `${selectedMonth}-${String(daysInMonth).padStart(2, "0")}`;

    setDatesLoading(true);
    setAvailableDates(null);
    setSelectedDate(null);
    setSlots([]);

    // Always use public endpoints for read-only slot/date queries (they work without auth)
    publicService.getAvailableDates(slug, from, to, selectedLocation.id)
      .then((res) => setAvailableDates(res.data))
      .catch(() => setAvailableDates([]))
      .finally(() => setDatesLoading(false));
  }, [slug, selectedLocation, selectedMonth, professional]);

  // When date changes → fetch time slots
  useEffect(() => {
    if (!slug || !selectedDate || !selectedLocation) {
      setSlots([]);
      return;
    }

    setSlotsLoading(true);
    publicService.getSlots(slug, selectedDate, selectedLocation.id)
      .then((res) => setSlots(res.data))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [slug, selectedDate, selectedLocation]);

  // Reset downstream state when location changes
  const handleLocationSelect = useCallback((location) => {
    setSelectedLocation(location);
    setSelectedService(null);
    setSelectedMonth(null);
    setAvailableDates(null);
    setSelectedDate(null);
    setSlots([]);
  }, []);

  // Reset downstream state when service changes
  const handleServiceSelect = useCallback((service) => {
    setSelectedService(service);
    setSelectedMonth(null);
    setAvailableDates(null);
    setSelectedDate(null);
    setSlots([]);
  }, []);

  // Book an appointment
  const bookAppointment = useCallback(async ({ dateTime, endTime, reason, attendeeName, rescheduleFromToken }) => {
    setIsBooking(true);
    try {
      if (mode === "auth") {
        // Authenticated booking — use the appointment service
        const res = await appointmentService.create({
          professionalId: professional.id,
          patientId,
          dateTime,
          endTime,
          reason: reason || undefined,
          attendeeName: attendeeName || undefined,
          locationId: selectedLocation?.id,
          serviceId: selectedService?.id || null,
        });
        return res.data;
      } else {
        // Public booking — uses slug-based endpoint (requires verified phone)
        const res = await publicService.bookAppointment({
          professionalSlug: slug,
          dateTime,
          endTime,
          reason: reason || undefined,
          attendeeName: attendeeName || undefined,
          locationId: selectedLocation?.id,
          serviceId: selectedService?.id || null,
          ...(rescheduleFromToken ? { rescheduleFromToken } : {}),
        });
        return res.data;
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Error al agendar la cita";
      toast.error(msg);
      throw err;
    } finally {
      setIsBooking(false);
    }
  }, [mode, slug, professional?.id, selectedLocation, selectedService]);

  // Reset the entire flow
  const reset = useCallback(() => {
    setSelectedLocation(null);
    setSelectedService(null);
    setSelectedMonth(null);
    setAvailableDates(null);
    setSelectedDate(null);
    setSlots([]);
  }, []);

  return {
    selectedLocation,
    handleLocationSelect,
    selectedService,
    handleServiceSelect,
    selectedMonth,
    setSelectedMonth,
    availableDates,
    datesLoading,
    selectedDate,
    setSelectedDate,
    slots,
    slotsLoading,
    bookAppointment,
    isBooking,
    reset,
    nextAvailable,
    nextAvailableLoading,
  };
}
